/**
 * Marketing site content for serviceapp.ai.
 *
 * Centralised so nothing is hardcoded inside components. When the platform
 * admin backend ships, this becomes the fallback for values served from
 * `platform_config` (pricing bands, trial length, contact email, etc.).
 */

import { EXCELLENT_FIT_INDUSTRY_CARDS } from './industrySeoExtras.js';

export const BRAND = {
  name: 'ServiceApp',
  domain: 'serviceapp.ai',
  tagline: 'Quote, send the crew, get paid',
  metaTitle: 'Field Service Software & Service Business App | ServiceApp.ai',
  metaDescription:
    'Field service software and service business app for job scheduling, WhatsApp quotes, crew dispatch and invoicing. Crew logins free. Grow trial, no card.',
  contactEmail: 'hello@serviceapp.ai',
  logo: '/serviceapp-logo.png',
  icon: '/serviceapp-favicon.png?v=light-20260827',
} as const;

export const TRIAL = {
  days: 60,
  requiresCard: false,
  ctaLabel: 'Start your 2-month free trial',
  ctaSubtext: '2 months full access · No credit card · Crew logins are free',
} as const;

/** Section headlines kept out of components for SEO + admin fallback. */
export const SECTIONS = {
  features: {
    title: 'Everything you need to win the job',
    intro:
      'Quote, book, send your crew and get paid in one app. No more copying the same job into three different tools.',
  },
  pricing: {
    eyebrow: 'Start with the app',
    title: 'The app, on its own',
    intro:
      'This price is for ServiceApp only. You pay for the people in the office. Cleaners and field crew log in free. Every plan can run jobs, quotes and monthly contracts.',
  },
  faq: {
    title: 'Common questions',
  },
  contact: {
    title: 'Talk to our team',
    body: 'Want a demo or help setting up? Book a call, or tell us how you run jobs today. We reply within one working day.',
  },
} as const;

export interface HeroOsModule {
  id: string;
  label: string;
  /** Label shown on hero chips — matches the in-app admin tab name. */
  adminTabLabel: string;
  shortLabel: string;
  icon: string;
  stat: string;
  /** When set, displayed via org currency instead of legacy `stat` for money metrics. */
  statAmount?: number;
  statLabel: string;
  detail: string;
  /** Screenshot shown when this admin tab is selected in the hero preview. */
  previewImage: string;
  previewCaption: string;
}

export interface HeroAiInsight {
  id: string;
  priority: 'high' | 'medium' | 'positive';
  title: string;
  metric: string;
  /** When set, displayed via org currency instead of legacy `metric` for money metrics. */
  metricAmount?: number;
  insight: string;
  action: string;
}

export interface HeroPreviewTab {
  id: string;
  label: string;
}

export interface HeroGallerySlide {
  id: string;
  trade: string;
  /** Optional legacy/OG still; hero carousel renders a live product mock instead */
  image?: string;
  alt: string;
  headline: string;
  caption: string;
  /** Industry SEO landing when the slide is clicked */
  seoPath?: string;
}

/** Trade-specific hero carousel — agency-style conversion story per trade. */
export const HERO_GALLERY: HeroGallerySlide[] = [
  {
    id: 'cleaning',
    trade: 'Cleaning',
    alt: 'WhatsApp cleaning quote the customer accepts, then booking and payment in ServiceApp',
    headline: 'Send the quote. Get the job.',
    caption: 'WhatsApp quotations with scope and add-ons — customers confirm in one tap',
    seoPath: '/cleaning-service-software',
  },
  {
    id: 'hvac',
    trade: 'AC & HVAC',
    alt: 'HVAC AMC quote accepted and technician dispatched in ServiceApp',
    headline: 'AMC contracts that renew themselves',
    caption: 'Schedule servicing, dispatch techs and collect before you leave the site',
    seoPath: '/hvac-service-software',
  },
  {
    id: 'pest',
    trade: 'Pest control',
    alt: 'Pest control route stop quoted, treated and follow-up booked in ServiceApp',
    headline: 'Routes that pay on time',
    caption: 'Treatments, follow-ups and service reports — invoiced automatically',
    seoPath: '/pest-control-software',
  },
  {
    id: 'plumbing',
    trade: 'Plumbing',
    alt: 'Emergency plumbing quote accepted and payment collected on site in ServiceApp',
    headline: 'Quote on site. Collect before you leave.',
    caption: 'Emergency calls become booked jobs and paid invoices in one flow',
    seoPath: '/plumbing-business-software',
  },
  {
    id: 'fitout',
    trade: 'Interior fit-out',
    alt: 'Interior fit-out milestone quote accepted and crew scheduled in ServiceApp',
    headline: 'Fit-out quotes that close milestones',
    caption: 'Measure, install, snag and get paid — crews and invoices in one place',
    seoPath: '/interior-fitout-software',
  },
  {
    id: 'roofing',
    trade: 'Roofing',
    alt: 'Roofing inspection quote accepted and crew scheduled in ServiceApp',
    headline: 'Inspections that become signed work',
    caption: 'Photo-backed quotes, crew schedules and invoices owners actually open',
    seoPath: '/roofing-business-software',
  },
  {
    id: 'pool',
    trade: 'Pool service',
    alt: 'Pool maintenance route auto-invoiced after service in ServiceApp',
    headline: 'Recurring routes. Automatic invoices.',
    caption: 'Weekly stops, reminders and payments without chasing customers',
    seoPath: '/pool-service-software',
  },
  {
    id: 'solar',
    trade: 'Solar',
    alt: 'Solar site survey quoted with deposit received in ServiceApp',
    headline: 'Surveys that land the install',
    caption: 'Site visits, install crews and service contracts in one app',
    seoPath: '/solar-installation-software',
  },
];

/** Homepage LCP — human-centered hero (desktop / default). */
export const HERO_LCP_IMAGE = '/serviceapp-hero-human.jpg';
/** Portrait crop for phones (picture source). */
export const HERO_LCP_IMAGE_MOBILE = '/serviceapp-hero-human-mobile.jpg';

export const HERO = {
  eyebrow: 'Field service software · Crew logins free · From $49/mo',
  headlineMain: 'Quote. Send the crew. Get paid.',
  headlineAccent: 'Sell jobs, products and memberships.',
  subheadline:
    'A service business app for the office and the van. Job scheduling, WhatsApp quotes and payments stay together, so work does not get lost in chat.',
  purposeEyebrow: 'Why this app exists',
  purposeLine:
    'Field service software that keeps every job in one place — from the first message to the paid invoice. Sell services, products and memberships. Your crew logs in free.',
  secondaryCta: 'See how it works',
  aiCta: 'See AI insights',
  proofLine: 'Crew logins are free · 60-day Grow trial · No card · No cut of what you sell',
  imageAlt:
    'Service technician shaking hands with a customer beside a work van — a calm, uncluttered scene for running jobs on ServiceApp',
  previewTitle: 'Your service business app',
  previewCaption: 'Real app — enquiries, jobs, teams and payments on one screen',
  previewTabs: [
    { id: 'operations', label: 'Operations' },
    { id: 'ai', label: 'AI Overview' },
    { id: 'growth', label: 'Growth' },
  ] as HeroPreviewTab[],
  osModules: [
    {
      id: 'enquiries',
      label: 'Enquiries',
      adminTabLabel: 'Enquiries',
      shortLabel: 'Enquiries',
      icon: 'phone',
      stat: '24',
      statLabel: 'open leads',
      detail: 'Every call, WhatsApp and web form lands in one list. Nothing gets lost.',
      previewImage: '/serviceapp-hero-dashboard.webp',
      previewCaption: 'Enquiries tab — every lead captured in one queue',
    },
    {
      id: 'quotes',
      label: 'Quotes',
      adminTabLabel: 'Prices',
      shortLabel: 'Prices',
      icon: 'messageCircle',
      stat: '12',
      statLabel: 'sent today',
      detail:
        'Send a clear WhatsApp quote with prices and extras. One tap turns it into a booking.',
      previewImage: '/serviceapp-workflow-visual.webp',
      previewCaption: 'Prices tab — detailed WhatsApp quotes in one tap',
    },
    {
      id: 'jobs',
      label: 'Bookings',
      adminTabLabel: 'Bookings',
      shortLabel: 'Bookings',
      icon: 'calendar',
      stat: '38',
      statLabel: 'jobs this week',
      detail: 'Drag jobs into the calendar, repeat regular work and send the right crew.',
      previewImage: '/serviceapp-hero-dashboard.webp',
      previewCaption: 'Bookings tab — schedule and dispatch your whole week',
    },
    {
      id: 'teams',
      label: 'Teams',
      adminTabLabel: 'Teams',
      shortLabel: 'Teams',
      icon: 'users',
      stat: '6',
      statLabel: 'crews active',
      detail: 'Your team sees today’s jobs, directions and job reports on their own phone.',
      previewImage: '/serviceapp-owner-cleaning.webp',
      previewCaption: 'Teams tab — crews see jobs and routes on any phone',
    },
    {
      id: 'comms',
      label: 'Email and messages',
      adminTabLabel: 'Communication',
      shortLabel: 'Comms',
      icon: 'mail',
      stat: '248',
      statLabel: 'emails sent',
      detail:
        'Email from your own business address. Inbox, updates and campaigns in one place.',
      previewImage: '/serviceapp-hero-dashboard.webp',
      previewCaption: 'Communication tab — email, campaigns and notifications',
    },
    {
      id: 'money',
      label: 'Payments',
      adminTabLabel: 'Payments',
      shortLabel: 'Payments',
      icon: 'creditCard',
      stat: '1.2L',
      statAmount: 120000,
      statLabel: 'collected week',
      detail: 'Invoices and payment links go out automatically. Get paid the same day.',
      previewImage: '/serviceapp-hero-dashboard.webp',
      previewCaption: 'Payments tab — invoices, links and reconciliation',
    },
    {
      id: 'ai',
      label: 'AI assistant',
      adminTabLabel: 'AI Chats',
      shortLabel: 'AI Chats',
      icon: 'bot',
      stat: '3',
      statLabel: 'actions today',
      detail: 'Replies to customers, drafts quotes and tells you what needs your attention.',
      previewImage: '/serviceapp-ai-visual.webp',
      previewCaption: 'AI chats — Ava replies, quotes and follows up on every plan',
    },
  ] as HeroOsModule[],
  aiInsights: [
    {
      id: 'revenue',
      priority: 'high',
      title: 'Revenue at risk',
      metric: '42K',
      metricAmount: 42000,
      insight: '3 quotes aging past 48 hours. AI recommends WhatsApp follow-up before competitors reply.',
      action: 'Send follow-ups',
    },
    {
      id: 'capacity',
      priority: 'medium',
      title: 'Crew capacity',
      metric: '87%',
      insight: 'Thursday is overbooked. Move 2 jobs to Friday to protect quality and margins.',
      action: 'Rebalance schedule',
    },
    {
      id: 'cash',
      priority: 'positive',
      title: 'Cash velocity',
      metric: '+18%',
      insight: 'Collections up vs last week. 4 invoices still open — chase before weekend.',
      action: 'View cash flow',
    },
    {
      id: 'growth',
      priority: 'positive',
      title: 'Growth signal',
      metric: '2.4×',
      insight: 'Repeat customers driving bookings. AI suggests a loyalty offer for top 20 clients.',
      action: 'Launch campaign',
    },
  ] as HeroAiInsight[],
  trustPoints: [
    { icon: 'clock', label: 'Live in minutes', sub: 'Pick your trade and your app is ready' },
    { icon: 'shield', label: 'Crew logins free', sub: 'Pay for office users, not the van' },
    { icon: 'bot', label: 'AI included', sub: 'Answers customers and shows what to fix' },
  ],
} as const;

export const PROBLEM = {
  title: 'Still running your business on WhatsApp and spreadsheets?',
  intro:
    'Most service businesses do good work and still lose money. Leads sit in one app, quotes in another, the schedule on paper and the money somewhere else.',
  imageCaption: 'Before ServiceApp: scattered tools, missed jobs, slow payments',
  pains: [
    {
      title: 'You lose jobs you already won',
      description:
        'A customer calls while you are on site. The note never turns into a quote, and someone else gets the job.',
    },
    {
      title: 'Nobody knows which quotes are live',
      description:
        'Prices agreed in chat or notes are hard to track. No one is sure who should follow up, or when.',
    },
    {
      title: 'You cannot see the week',
      description:
        'You do not know who is where or what is confirmed. Double bookings and empty slots become normal.',
    },
    {
      title: 'You get paid late',
      description:
        'Invoices written days after the job and chased by phone hurt your cash. You paid for the work before the customer did.',
    },
  ],
} as const;

/** Short explanation for owners, shown before the longer job-by-job walkthrough. */
export const PLAIN_STORY = {
  title: 'What is ServiceApp?',
  purposeEyebrow: 'Why this app exists',
  purpose:
    'ServiceApp is field service software so a service business can sell the work, schedule jobs, send the crew and get paid without spreading the job across WhatsApp, a notebook and a spreadsheet.',
  body: 'It is a service business app for the office and the crew. Job scheduling, WhatsApp quotes, invoicing, products, memberships and payments stay together. Your crew uses the field technician app on their phone. You see the day from the office. Crew logins are free. You pay only for office users.',
  howTitle: 'How it works',
  steps: [
    {
      title: 'A customer gets in touch',
      description:
        'They call, message you on WhatsApp, or fill in a form. Their name and what they need are saved. You do not have to write it in a notebook.',
    },
    {
      title: 'You send a price and book the job',
      description:
        'You send a quote. When they say yes, the job is booked. Your crew sees the address and the time on their phone.',
    },
    {
      title: 'The work is done and you get paid',
      description:
        'When the job is finished, the invoice goes out. The customer can pay from a link. You can see who still owes you.',
    },
  ],
} as const;

export const SOLUTION = {
  title: 'One place for quotes, jobs and payments',
  body: 'ServiceApp holds your leads, quotes, bookings, crews and invoices in one app. Your team stops copying the same job between tools, and you can see what is happening today.',
  imageCaption: 'One flow: enquiry to quote to job to payment',
  ctaLabel: 'Explore the business app',
  ctaPath: '/business-app',
  outcomes: [
    'Every enquiry in one list, from phone, WhatsApp or your website',
    'Clear WhatsApp quotes customers can accept with one tap',
    'Confirmations and reminders so fewer customers forget the visit',
    'Every job scheduled, sent to a crew and updated from site',
    'Plain advice on what to fix, plus real people to help you set up',
  ],
} as const;

/** The core enquiry-to-payment workflow, rendered as a timeline. */
export const WORKFLOW = {
  title: 'How a job flows through ServiceApp',
  intro:
    'From the first call to money in the bank, in one flow. Nothing to copy between apps.',
  imageCaption: 'Every step from enquiry to payment, in one app',
  steps: [
    {
      title: 'Lead comes in',
      description:
        'Phone call, WhatsApp or website form — the enquiry is logged with customer details, service needed and location in seconds.',
    },
    {
      title: 'Quote sent on WhatsApp',
      description:
        'Send a branded quotation with clear scope, exclusions and add-ons in under a minute — where your customer already replies.',
    },
    {
      title: 'Customer confirms',
      description:
        'One tap turns the accepted quote into a confirmed booking. Automatic confirmation messages keep everyone aligned.',
    },
    {
      title: 'Reminders before the job',
      description:
        'Service reminders go out before the visit so customers show up prepared — fewer no-shows, more completed jobs.',
    },
    {
      title: 'Crew assigned',
      description:
        'Schedule the job and assign the right team. Everyone sees their day, jobs and route on their phone.',
    },
    {
      title: 'Job updated live',
      description:
        'Progress, photos and the service report are completed on site — the office always knows where things stand.',
    },
    {
      title: 'Invoice and payment link',
      description:
        'When the job is done, a branded invoice and payment link go out automatically — numbered and tax-compliant.',
    },
    {
      title: 'Customer portal',
      description:
        "Quotes, bookings, reports and invoices live in your customer's own dashboard. Fewer status calls for you.",
    },
  ],
} as const;

export interface FeatureItem {
  icon: string; // lucide icon key resolved in the component
  title: string;
  description: string;
}

export const FEATURES: FeatureItem[] = [
  {
    icon: 'phone',
    title: 'Catch every lead',
    description:
      'Calls, WhatsApp messages and website forms all land in one list, with the customer and job history attached. Nothing stays in a notebook.',
  },
  {
    icon: 'messageCircle',
    title: 'WhatsApp quotes that win work',
    description:
      'Send a proper quote in under a minute, with what is included, what is not and any extras. The customer accepts with one tap and it becomes a booking.',
  },
  {
    icon: 'bell',
    title: 'Confirmations and reminders',
    description:
      'Customers get a confirmation when they book and a reminder before the visit. Fewer people forget, so more jobs actually happen.',
  },
  {
    icon: 'calendar',
    title: 'Plan the week and send the crew',
    description:
      'Drag jobs into the calendar, set up repeat work and service contracts, and see who is free tomorrow on one screen.',
  },
  {
    icon: 'users',
    title: 'Keep your crew in the loop',
    description:
      'Your team sees their jobs on any phone. They add photos and finish the job report on site, so the office knows where things stand.',
  },
  {
    icon: 'fileText',
    title: 'Invoice as soon as the job is done',
    description:
      'Invoices carry your branding, your tax rules (GST, VAT or sales tax) and numbers that run in order. They come from finished jobs, not from memory days later.',
  },
  {
    icon: 'creditCard',
    title: 'Get paid faster',
    description:
      'Every invoice carries a payment link. Receipts and reminders are handled, so the money arrives while the job is still fresh.',
  },
  {
    icon: 'mail',
    title: 'Business email built in',
    description:
      'Email from your own business address. Customer replies, updates and campaigns sit next to the jobs they belong to.',
  },
  {
    icon: 'bot',
    title: 'AI that answers and advises',
    description:
      'Ava replies to customers on your booking page and chat link. For you, the app reads your own numbers and says what to chase or fix this week.',
  },
];

export const INDUSTRIES_SECTION = {
  title: 'Built for trades that quote, schedule and invoice',
  intro:
    'Cleaning, air conditioning, pest control, plumbing, interiors, facilities and dozens more. Pick your trade at setup and the app arrives ready for your kind of work.',
  imageCaption: 'Trusted by service businesses across cleaning, HVAC, interiors, trades and more',
  seeAllPath: '/industries',
  seeAllLabel: 'See all industries',
  industries: EXCELLENT_FIT_INDUSTRY_CARDS,
} as const;

export const AI_SECTION = {
  eyebrow: 'AI assistant included on every plan',
  title: 'Ava answers customers when you can’t',
  body: 'Ava sits on your booking page, your website and your chat link. She answers using your real services and prices. Every plan includes a set number of AI messages, and you can buy more when you need them.',
  imageCaption: 'Ava helps customers book using your own services and prices',
  capabilities: [
    {
      title: 'Answers with your prices',
      description:
        'Ava uses your services, prices and free slots. She is not a generic chatbot reading a website.',
    },
    {
      title: 'Helps customers book',
      description:
        'She explains the options and guides the customer to a booking. You still approve the work.',
    },
    {
      title: 'Follow-ups you control',
      description:
        'Turn on reminders for quotes and invoices, and choose whether they send or wait for your approval.',
    },
    {
      title: 'Feeds your weekly advice',
      description:
        'What Ava hears joins the rest of your job data, so the advice you get is based on real conversations.',
    },
  ],
} as const;

/**
 * Plain-language insights: what to chase and fix, from the org's own job data.
 * Personal help: setup guidance, dedicated support, demo & configuration calls.
 * Override via app_config `saas_landing_business_intelligence`.
 */
export const BI_SECTION = {
  eyebrow: 'Run the jobs first, then see what to improve',
  title: 'See what is costing you money',
  body: 'Once your jobs, quotes and payments are in ServiceApp, the app reads your own numbers and tells you where work and money are slipping away.',
  promise:
    'This is not generic advice. It looks at your enquiries, your quotes, your bookings and your cash, then points at the few things worth fixing.',
  consultationOffer:
    'You also get real people. Book a call for a demo, a deeper look at your numbers or help setting things up, any time after you start.',
  isoLine:
    'Some plans include a Quality area for controlled documents, corrective actions and internal audits, alongside everyday proof from jobs, checklists and customer records. We are not an auditor. Use it to get your evidence in order, then work with an accredited auditor.',
  imageCaption: 'Your dashboard shows what to chase and what to fix next',
  paths: [
    {
      id: 'ai',
      title: 'Reports you will actually read',
      description:
        'Which quotes are going cold, which customers went quiet, which invoices are unpaid and where your cash is drifting.',
    },
    {
      id: 'actions',
      title: 'A clear next step',
      description:
        'Every point comes with something to do: who to call, what to reprice, which day is overbooked.',
    },
  ],
  pillars: [
    {
      title: 'Win more of the work you quote',
      description:
        'Clear WhatsApp quotes, confirmations and reminders. See which leads turn into jobs and which quotes stall.',
    },
    {
      title: 'Keep cash healthy',
      description:
        'Spot unpaid invoices, quiet customers and rising costs early, while you can still do something about it.',
    },
    {
      title: 'Advice in plain words',
      description:
        'A short list of things to fix this week, based on your own jobs. Not a wall of charts.',
    },
    {
      title: 'Help from real people',
      description:
        'Demos, setup help and someone to walk you through it. Book a call whenever you want one.',
    },
  ],
  experts: ['One app for the work', 'Advice in plain words', 'Dedicated support', 'Demo and setup calls'],
  ctaPrimary: 'Start free trial',
  ctaSecondary: 'Book a demo or support call',
} as const;

/**
 * Positioning for owners, CEOs and ops managers — the people accountable for
 * sales, dispatch, cash and customer experience.
 */
export const OWNER_SECTION = {
  eyebrow: 'Built for owners and managers',
  title: 'ServiceApp is your office when you are on site',
  body: 'Whether you work alone, run a small partnership or manage crews, you need one place for leads, the schedule and the money. ServiceApp does the office work so you can win jobs and finish them well.',
  imageCaption: 'One view: sales, schedule, invoices and follow-ups',
  pillars: [
    {
      icon: 'phone',
      title: 'Run it from your phone, between jobs',
      description:
        "Quote from the customer's driveway, book the next job on the move and send the invoice before you leave. No back-to-the-office admin at night.",
    },
    {
      icon: 'bot',
      title: 'Ava takes the chats you cannot',
      description:
        'Ava answers on your booking page and chat link using your own prices, while you are under a sink or on a roof. Every plan includes AI messages.',
    },
    {
      icon: 'creditCard',
      title: 'Start free, pay when it is worth it',
      description:
        'Start is $49/mo. Every workspace gets a 2-month Grow trial first, no card. Unlimited crew — you pay only for office users.',
    },
    {
      icon: 'fileText',
      title: 'Look bigger than you are',
      description:
        'Smart quotes, proper invoices and a customer login make a small team look established, so bigger clients trust you with regular work.',
    },
    {
      icon: 'users',
      title: 'One picture for partners and managers',
      description:
        'Everyone sees the same jobs, customers and money. No more “who spoke to them?” and no more double bookings. Adding crew is free.',
    },
    {
      icon: 'calendar',
      title: 'Nothing slips while you are on site',
      description:
        'Every enquiry, quote, job and payment is recorded for you. The work that used to live in your head now lives in the app.',
    },
  ],
} as const;

export interface PricingBand {
  id: string;
  name: string;
  monthlyPriceUsd: number | null; // null = custom pricing
  bookingsPerMonth: string;
  description: string;
  highlights: string[];
  featured?: boolean;
}

/** Seat-based licensing: office users billed, crew free. Fallback when API catalog is unavailable. */
export const PRICING_BANDS: PricingBand[] = [
  {
    id: 'start',
    name: 'Start',
    monthlyPriceUsd: 49,
    bookingsPerMonth: '3 office users',
    description: 'For a small office. Quotes, bookings, jobs, invoices and monthly contracts. Your crew logs in free.',
    highlights: [
      '3 office users · unlimited crew',
      'Jobs, quotes and monthly contracts',
      'Quotes, bookings, invoices, payments',
      'Custom domain, automations & API',
      'AI assistant included (50 messages/mo) · email packs available',
    ],
  },
  {
    id: 'grow',
    name: 'Grow',
    monthlyPriceUsd: 149,
    bookingsPerMonth: '10 office users',
    description: 'For a growing team. Everything in Start, plus staff, payroll and vehicle tracking. Your crew logs in free.',
    featured: true,
    highlights: [
      '10 office users · unlimited crew',
      'Everything in Start',
      'HR, payroll and attendance',
      'Phone tracking and advanced dashboards',
      'AI assistant included (200 messages/mo) · email packs available',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyPriceUsd: 299,
    bookingsPerMonth: '25 office users',
    description: 'For a larger company. Everything in Grow, plus quality checks, service reports and matching the bank. Your crew logs in free.',
    highlights: [
      '25 office users · unlimited crew',
      'Everything in Grow',
      'Quality, SLA and audit reports',
      'Bank reconciliation',
      'AI assistant included (500 messages/mo) · email packs available',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthlyPriceUsd: null,
    bookingsPerMonth: 'Unlimited office users',
    description: 'For more than one branch. Everything in Pro, with no cap on office people and someone to help you set up.',
    highlights: [
      'Unlimited office users',
      'Everything in Pro',
      'SSO and advanced security',
      'Dedicated onboarding and success manager',
      'Full AI included (customizable allowance) · email packs available',
    ],
  },
];

/** Repeat visits and AMC plans that already exist in the app. */
export const RECURRING_PLANS = {
  title: 'Turn one-off jobs into monthly work',
  description:
    'ServiceApp books repeat visits for cleaning plans, pest-control packages and AC care. The next visit goes on the calendar, and you can send the customer a WhatsApp message.',
  intro:
    'This is the recurring-services area already in the app. You set how often the visit repeats. The crew sees each visit. You are not buying a separate product.',
  points: [
    {
      title: 'A plan for the visits',
      body: 'Pick the service, how often it repeats, and what is included. Add-ons can repeat on every visit.',
    },
    {
      title: 'The next visits are already on the calendar',
      body: 'Monthly, quarterly, twice a year, or once a year. You can pause a plan when a customer asks you to stop.',
    },
    {
      title: 'Ask to be paid on the same rhythm',
      body: 'Payment terms sit on the plan. When a visit is done, the invoice can go out with a pay link.',
    },
  ],
  faqs: [
    {
      q: 'Is this a new subscription product?',
      a: 'No. It is the recurring services and AMC visits already in ServiceApp. A cleaning plan, a pest-control package, or an AC care contract is a repeating job with a price.',
    },
    {
      q: 'Can the customer see the next visit?',
      a: 'Yes. The visit is a normal job on the calendar, and you can send the details on WhatsApp.',
    },
    {
      q: 'Does this replace Start, Grow or Pro?',
      a: 'No. Repeat visits are part of the app. You still pick Start, Grow or Pro for the office. Your crew logs in free.',
    },
  ],
} as const;

export const FAQS = [
  {
    q: 'What is ServiceApp.ai?',
    a: 'ServiceApp is field service software and a service business app. Quote, schedule jobs, dispatch your crew and get paid in one place. Sell services, products and memberships — together or on their own. Crew logins are free. You pay for office users. It also tells you what to chase each week, and real people help you set it up.',
  },
  {
    q: 'What is the best service business app for a small service company?',
    a: 'A good one covers the whole job: the lead, the quote, the booking, the crew, the invoice and the payment. ServiceApp does that for cleaning, air conditioning, pest control, plumbing and handyman businesses. Crew logins are free, Start is $49/mo, and you do not need a card to try it.',
  },
  {
    q: 'What is the best field service software for a small service business?',
    a: 'Look for software that runs the office as well as the field: customer records, WhatsApp quotes, booking confirmations, reminders, scheduling, job reports on the phone, invoices and payments. ServiceApp does all of this. You pay for office users; crew and field logins stay free.',
  },
  {
    q: 'How do I run a service business without drowning in admin?',
    a: 'Keep every enquiry, quote, job and invoice in one app instead of WhatsApp threads and spreadsheets. ServiceApp turns accepted quotes into booked jobs, sends the confirmations and reminders, and turns finished jobs into invoices. You spend your time winning and doing the work.',
  },
  {
    q: 'Is ServiceApp only for field work, or does it run the office too?',
    a: 'Both, in plain terms: it runs the field work and the office. Scheduling, crew dispatch and job reports on the phone, plus customer records, quotes, reminders, invoices, payments and weekly advice. Cleaning companies, trades and facility contractors use it as their main app every day.',
  },
  {
    q: 'How long does it take to set up ServiceApp?',
    a: 'A few minutes. Sign up, tell us your company name, your trade and your country, and your app arrives with services and prices already suggested for your trade. Your booking page is live straight away. You can change anything later, and we can walk you through it on a call.',
  },
  {
    q: 'How does ServiceApp help me automate my service company?',
    a: 'The routine chasing happens for you. Accepted quotes become booked jobs, finished jobs become invoices, payment reminders go out on their own and regular work is scheduled ahead. Ava answers customers on your booking page and chat link using your own prices.',
  },
  {
    q: 'How is ServiceApp data-driven for owners and ops managers?',
    a: 'Every quote, job, invoice and payment updates one dashboard, so you see revenue, money owed and how many quotes turn into jobs without building a spreadsheet. The app also points out what needs you: quotes to chase, customers who went quiet, days that are overbooked and cash running short.',
  },
  {
    q: 'What insights does ServiceApp give owners?',
    a: 'It reads your own jobs, quotes and payments and gives you a short list: which quotes are going cold, which customers have gone quiet, which invoices are unpaid and where money is leaking. Each one comes with a next step. You can also book a call to go through it with someone.',
  },
  {
    q: 'Can ServiceApp help us prepare for ISO 9001?',
    a: 'Yes. On some plans the Quality area keeps controlled documents, corrective actions and audit records, while your everyday jobs, checklists and customer records provide the proof. We are not an auditor, so an accredited auditor still does the certification itself.',
  },
  {
    q: 'Who is ServiceApp.ai built for?',
    a: 'Owners and managers who quote work, schedule people and invoice customers. It suits cleaning, pest control, air conditioning, plumbing, electrical, handyman, facility management and landscaping businesses, and any team that wants sales, jobs and money in one place.',
  },
  {
    q: 'Is ServiceApp good for a single owner or a small partnership?',
    a: 'Yes. When you work alone or with a couple of partners, you are the sales team, the dispatcher and the accounts department. ServiceApp handles that office work, and partners all see the same jobs and money. Start is $49/mo, and every new account gets a Grow trial first.',
  },
  {
    q: 'Why subscribe instead of staying on WhatsApp and spreadsheets?',
    a: 'WhatsApp and spreadsheets feel free until one missed lead, one double booking or one late invoice costs more than a month of software. ServiceApp keeps that from happening. Unlimited crew. Pay only for office users. It pays for itself in jobs you would have lost.',
  },
  {
    q: 'How is ServiceApp different from other field service or small business software?',
    a: 'Most tools do one part of the job, such as invoicing or scheduling, and many charge for every login. ServiceApp covers the whole thing from first enquiry to payment. Unlimited crew. Pay only for office users. Quotes go out on WhatsApp, Ava answers customers, and real people help you set up.',
  },
  {
    q: 'Does ServiceApp work as cleaning service software?',
    a: 'Yes. Cleaning companies use it for regular home and office contracts, crew rotas, room checklists, WhatsApp quotes and same-day invoices. See /cleaning-service-software and /cleaning-service-app.',
  },
  {
    q: 'How do WhatsApp quotations work?',
    a: 'You build a quote that shows what is included, what is not and any extras, then send it on WhatsApp or by email in one tap. The customer opens it with your branding and accepts with one tap, which books the job. The confirmation and reminder go out after that.',
  },
  {
    q: 'Can I book a demo or get configuration support?',
    a: 'Yes. Once you have an account you can book a call for a demo, a look at your numbers, or help setting things up. Someone will go through it with you rather than leaving you to work it out.',
  },
  {
    q: 'Do my customers get their own dashboard?',
    a: "Yes. Customers see their quotes, bookings, service reports and invoices in a simple portal — and can pay online. Fewer “what's the status?” calls for you.",
  },
  {
    q: 'How does the free trial work?',
    a: 'Every new workspace gets a Grow trial — jobs, quotes, monthly contracts and payments on real work. No card needed. The length is set by the platform (today it is 2 months). After the trial, pick Start, Grow or Pro. Your crew logs in free. Start is $49/mo.',
  },
  {
    q: 'Is pricing per user?',
    a: 'You pay for office users only. Unlimited crew. Field and crew logins stay free on every plan. Extra office seats can be added on Grow when that price is live; until then, upgrade to Grow.',
  },
  {
    q: 'Does it work on mobile for field crews?',
    a: 'Yes. Your team opens it in the phone browser, with nothing to download. They see today’s jobs, directions and customer details, and can add photos and finish the job report on site.',
  },
  {
    q: "Does it handle my country's taxes and invoices?",
    a: 'Yes. Choose your country at setup and the app uses the right tax (GST, VAT or sales tax), currency, phone format and invoice rules for you.',
  },
  {
    q: 'Is my business data safe?',
    a: 'Yes. Your data is kept separate from every other company, stored encrypted and backed up continuously. You decide who on your team sees what. Your customer list and your prices stay yours.',
  },
  {
    q: 'Can I buy a website with ServiceApp?',
    a: 'Yes. Site Starter, Site Business, Site Business Plus and Site Premium each include a website and ServiceApp on one monthly fee, plus a one-time setup. Hosting and care are included. For the first 3 months you pay only for the website. The app itself follows the Grow trial. Association members of a partner such as PCPA Keralam see a lower monthly price. Ask us from the pricing page.',
  },
] as const;

export const FINAL_CTA = {
  title: 'Start quoting and getting paid this week',
  body: 'Try it free for two months on your own jobs. Quote, send the crew and get paid in one app. Crew logins stay free. Book a demo if you want a walkthrough.',
  consultCta: 'Book a demo or support call',
} as const;

/**
 * High-intent FAQs for FAQPage JSON-LD only (keeps schema tight).
 * Full FAQS still render on the homepage for long-tail coverage.
 */
export const FAQS_FOR_JSON_LD = [
  FAQS[0],
  FAQS[1],
  FAQS[2],
  FAQS[3],
  FAQS[4],
  FAQS[5],
  FAQS[10],
  FAQS[12],
  FAQS[17],
  FAQS[18],
] as const;

export const FOOTER_DESCRIPTION =
  'One app for service businesses. WhatsApp quotes, booking confirmations, reminders, crew scheduling, invoices and payments, with real people to help you set up.';
